import { useCallback, useEffect, useRef, useState, type ReactElement } from 'react';
import { Link, useOutletContext, useParams } from 'react-router';
import { useChargement } from '../coquille/useChargement.js';
import { deployerVersion, lancerExecution,
         type ResultatDeploiement } from '../automatisations.js';
import { EchecApi } from '../api.js';
import { CHEMINS } from '../routes.js';

interface Espace { readonly code: string; readonly nom: string; readonly role: string }

type Nature = 'test' | 'production';

/** L ORDRE EST DELIBERE : TEST d abord, PRODUCTION ensuite -- on prepare avant de mettre en service. */
const NATURES: readonly Nature[] = ['test', 'production'];

interface LigneVersion {
  readonly id: string;
  // DETTE 73 : NOMBRE ici, CHAINE dans VersionPubliee. Fidele aux deux
  // reponses moteur, qui divergent sur le meme concept. On ne convertit pas.
  readonly numero: number;
  readonly empreinte: string;
  readonly publie_le: string;
  readonly notes: string | null;
  readonly deploiements: readonly string[];
}

interface ReponseVersions { readonly versions: readonly LigneVersion[] }

type Lancement =
  | { readonly etat: 'repos' }
  | { readonly etat: 'en_vol'; readonly nature: Nature }
  | { readonly etat: 'fait'; readonly nature: Nature; readonly id: string }
  | { readonly etat: 'echec'; readonly message: string };
type Envoi =
  | { readonly etat: 'repos' }
  | { readonly etat: 'en_vol'; readonly cle: string }
  | { readonly etat: 'fait'; readonly r: ResultatDeploiement; readonly nature: Nature }
  | { readonly etat: 'echec'; readonly message: string };

/**
 * LES VERSIONS D UNE AUTOMATISATION, ET LEUR DEPLOIEMENT [29-G ph.3].
 *
 * ╔═══════════════════════════════════════════════════════════════════════╗
 * ║ DEPLOYE N EST PAS EXECUTANT.                                          ║
 * ╚═══════════════════════════════════════════════════════════════════════╝
 *
 * Deployer en TEST enregistre et historise un choix ; RIEN ne s execute.
 * Seul PRODUCTION materialise webhooks et planifications. L ecran doit donc
 * dire deploye, jamais actif ni en service -- ces deux mots-la appartiennent
 * a l etat vivant, que porte deja l ecran des automatisations.
 *
 * C EST DE LA PRESENTATION, PAS UNE TROISIEME VERITE : l API rend
 * `deploiements`, un tableau de natures. On le traduit, on ne le decide pas.
 * Meme discipline qu etatAutomatisation.ts.
 *
 * ── LA COULEUR PORTE LA MOITIE DU MESSAGE ───────────────────────────────
 *
 * PRODUCTION prend l etiquette de succes : c est ce qui tourne. TEST prend
 * l etiquette NEUTRE. Une pastille verte sur TEST laisserait croire a une
 * execution, ce que toute la phase 2 s est employee a rendre faux.
 *
 * ── UNE LISTE VIDE N EST PAS UNE PANNE ──────────────────────────────────
 *
 * `useChargement` ne modelise pas d etat vide : c est une INTERPRETATION de
 * la donnee, faite ici. Sans message explicite, une liste vide sans
 * explication laisse croire a un defaut du service.
 *
 * ── LE MASQUAGE N AUTORISE PAS ──────────────────────────────────────────
 *
 * Le geste exige `automatisation.activer`, donnee des administrateur. On ne
 * propose pas un bouton qui sera refuse ; la ROUTE reste l autorite, et un
 * controle cote navigateur se contourne avec un outil de developpement.
 */
export function Versions(): ReactElement {
  const espace = useOutletContext<Espace>();
  const { projet, id } = useParams<{ projet: string; id: string }>();
  const p = projet ?? '';
  const a = id ?? '';
  const { etat, recharger } = useChargement<ReponseVersions>(
    `/locataires/${encodeURIComponent(espace.code)}`
    + `/projets/${encodeURIComponent(p)}`
    + `/automatisations/${encodeURIComponent(a)}/versions`);
  const [envoi, poserEnvoi] = useState<Envoi>({ etat: 'repos' });
  // LA REPONSE TARDIVE N ECRIT PAS SUR UN ECRAN QUITTE. useChargement le fait
  // deja pour la LECTURE ; la mutation doit se garder elle-meme.
  const monte = useRef(true);
  useEffect(() => () => { monte.current = false; }, []);
  const peutDeployer = espace.role === 'administrateur' || espace.role === 'proprietaire';
  // `execution.declencher` vaut des l EDITEUR : preparer et essayer releve de
  // l editeur, mettre en service releve de l administrateur. L asymetrie DIT
  // le modele. La route reste l autorite ; ceci n est qu ergonomique.
  const peutLancer = espace.role !== 'lecteur';
  const [lancement, poserLancement] = useState<Lancement>({ etat: 'repos' });
  const surLancer = useCallback((nature: Nature) => {
    poserLancement({ etat: 'en_vol', nature });
    void (async () => {
      try {
        const r = await lancerExecution(espace.code, p, a, nature);
        if (!monte.current) return;
        poserLancement({ etat: 'fait', nature, id: r.execution_id });
      } catch (e) {
        if (!monte.current) return;
        poserLancement({ etat: 'echec', message: e instanceof EchecApi
          ? e.detail.message : 'Le service est injoignable.' });
      }
    })();
  }, [espace.code, p, a]);

  const surDeployer = useCallback((versionId: string, nature: Nature) => {
    poserEnvoi({ etat: 'en_vol', cle: versionId + nature });
    void (async () => {
      try {
        const r = await deployerVersion(espace.code, p, a, versionId, nature);
        if (!monte.current) return;
        poserEnvoi({ etat: 'fait', r, nature });
        recharger();
      } catch (e) {
        if (!monte.current) return;
        poserEnvoi({ etat: 'echec', message: e instanceof EchecApi
          ? e.detail.message : 'Le service est injoignable.' });
      }
    })();
  }, [espace.code, p, a, recharger]);

  return (
    <>
      <div className="contenu__entete">
        <div>
          <h1>Versions</h1>
          <p className="discret">Ce qui est publié, et où c’est déployé.</p>
        </div>
        <p><Link to={CHEMINS.editeur(espace.code, p, a)}
          className="bouton bouton--discret">Ouvrir l’éditeur</Link></p>
      </div>

      {peutLancer && etat.phase === 'pret' && etat.donnees.versions.length > 0 && (
        <div className="carte">
          <h2>Exécuter</h2>
          <p className="discret">
            Lance la version actuellement déployée dans l’environnement choisi.
            Ce n’est pas une version de l’historique qui s’exécute, mais celle
            qui y est en place.
          </p>
          {NATURES.map((nature) => {
            const deploye = etat.donnees.versions.some(
              (v) => v.deploiements.includes(nature));
            return (
              <button type="button" key={nature}
                      className="bouton"
                      disabled={!deploye || lancement.etat === 'en_vol'}
                      onClick={() => { surLancer(nature); }}>
                Exécuter en {nature === 'test' ? 'TEST' : 'PRODUCTION'}
              </button>
            );
          })}
          {lancement.etat === 'echec' && (
            <p className="message message--refus" role="alert">{lancement.message}</p>
          )}
          {lancement.etat === 'fait' && (
            <p className="message message--succes" role="status">
              Exécution lancée en {lancement.nature === 'test' ? 'TEST' : 'PRODUCTION'}.
            </p>
          )}
        </div>
      )}
      {etat.phase === 'chargement' && <p className="discret">Chargement…</p>}

      {etat.phase === 'echec' && (
        <div className="carte">
          <p className={etat.code === 'droit_insuffisant'
            ? 'message message--alerte' : 'message message--refus'} role="alert">
            {etat.message}
          </p>
          {etat.code !== 'droit_insuffisant' && (
            <button type="button" className="bouton"
                    style={{ marginTop: 'var(--espace-4)' }}
                    onClick={recharger}>Réessayer</button>
          )}
        </div>
      )}

      {etat.phase === 'pret' && etat.donnees.versions.length === 0 && (
        <div className="carte etat-vide">
          <h3>Aucune version publiée</h3>
          <p className="discret">
            Publiez une version depuis l’éditeur pour pouvoir la déployer.
          </p>
          <p><Link to={CHEMINS.editeur(espace.code, p, a)}
            className="bouton">Ouvrir l’éditeur</Link></p>
        </div>
      )}

      {envoi.etat === 'echec' && (
        <p className="message message--refus" role="alert">{envoi.message}</p>
      )}
      {envoi.etat === 'fait' && (
        <Resultat r={envoi.r} nature={envoi.nature} />
      )}

      {etat.phase === 'pret' && etat.donnees.versions.length > 0 && (
        <ul className="grille">
          {etat.donnees.versions.map((v) => (
            <CarteVersion key={v.id} v={v} envoi={envoi}
                          peutDeployer={peutDeployer} surDeployer={surDeployer} />
          ))}
        </ul>
      )}
    </>
  );
}

/** TEST est NEUTRE, PRODUCTION porte le succes : la couleur dit ce qui tourne. */
/**
 * LA DATE BRUTE DU MOTEUR EST ILLISIBLE. `publie_le` arrive en texte SQL
 * (2026-09-04 20:27:48.083253+00). On le rend localement, avec REPLI sur la
 * valeur brute : une date mal formee doit rester VISIBLE, pas disparaitre.
 */
function dateLisible(brut: string): string {
  // POSTGRES REND `+00`, PAS `+00:00` NI `Z` -- et un decalage sans minutes
  // n est pas de l ISO 8601 valide : new Date() rend NaN. Mesure sur la
  // donnee reelle, invisible a tsc et a une preuve qui lit le fichier.
  const d = new Date(brut.replace(' ', 'T').replace(/\+00$/, 'Z'));
  return Number.isNaN(d.getTime()) ? brut : d.toLocaleString('fr-BE');
}

const ETIQUETTE: Record<string, { readonly classe: string; readonly titre: string }> = {
  test: { classe: 'etiquette', titre: 'Déployée en TEST — enregistrée, mais rien ne s’exécute' },
  production: { classe: 'etiquette etiquette--succes', titre: 'Déployée en PRODUCTION — c’est ce qui s’exécute' },
};

function CarteVersion({ v, envoi, peutDeployer, surDeployer }: {
  v: LigneVersion; envoi: Envoi; peutDeployer: boolean;
  surDeployer: (versionId: string, nature: Nature) => void;
}): ReactElement {
  return (
    <li className="carte">
      <strong>Version {v.numero}</strong>
      <p className="tres-discret">Publiée le {dateLisible(v.publie_le)}</p>
      {v.notes !== null && v.notes.length > 0 && <p className="discret">{v.notes}</p>}
      <p>
        {v.deploiements.length === 0
          ? <span className="tres-discret">Déployée nulle part</span>
          : v.deploiements.map((n) => (
              <span key={n} className={ETIQUETTE[n]?.classe ?? 'etiquette'}
                    title={ETIQUETTE[n]?.titre ?? n}>{n.toUpperCase()}</span>
            ))}
      </p>
      {peutDeployer && (
        <div style={{ marginTop: 'auto', paddingTop: 'var(--espace-2)' }}>
          {NATURES.map((n) => {
            const deja = v.deploiements.includes(n);
            const vol = envoi.etat === 'en_vol' && envoi.cle === v.id + n;
            return (
              <button key={n} type="button"
                      className={n === 'production' ? 'bouton bouton--principal' : 'bouton'}
                      disabled={deja || envoi.etat === 'en_vol'}
                      title={deja ? 'Cette version y est déjà déployée.' : ''}
                      onClick={() => { surDeployer(v.id, n); }}>
                {vol ? 'Déploiement…' : 'Déployer en ' + n.toUpperCase()}
              </button>
            );
          })}
          {NATURES.every((n) => v.deploiements.includes(n)) && (
            <p className="tres-discret">Déployée dans les deux environnements.</p>
          )}
        </div>
      )}
    </li>
  );
}

/**
 * LE RETOUR DU DEPLOIEMENT.
 *
 * EN TEST, `webhooks` EST TOUJOURS VIDE -- aucun secret n existe pour TEST.
 * Le taire laisserait croire a un oubli du moteur ; on le DIT, avec sa raison.
 *
 * LE SECRET NE S AFFICHE QU ICI ET QU UNE FOIS. Aucune route ne le relit.
 */
function Resultat({ r, nature }: {
  r: ResultatDeploiement; nature: Nature;
}): ReactElement {
  const crees = r.webhooks.filter((w) => w.statut === 'cree');
  return (
    <div className="carte">
      <p className="message message--succes" role="status">
        Version {r.numero} déployée en {nature.toUpperCase()}
        {r.changee ? '' : ' (elle y était déjà)'}.
      </p>
      {nature === 'test' && (
        <p className="discret">
          Rien ne s’exécute : TEST enregistre le déploiement, sans créer de
          webhook ni de planification.
        </p>
      )}
      {crees.length > 0 && (
        <>
          <p className="message message--alerte" role="alert">
            Notez ces secrets maintenant : ils ne seront plus jamais affichés.
          </p>
          <ul>
            {crees.map((w) => (
              <li key={w.cle_noeud}><code>{w.cle_noeud}</code> — <code>{w.secret}</code></li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}
